import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchIndex, type IndexRow } from '../api/seoTracking';
import { SeoTrackingDetail } from './SeoTrackingDetail';

type Section = 'all' | 'landing' | 'blog' | 'docs';
type PriorityFilter = 'all' | 'has-p0' | 'has-p1' | 'has-needs-resuggest';
type SortKey = 'priority' | 'clicks' | 'impressions' | 'ctr' | 'position' | 'issues' | 'closed' | 'needs-resuggest';

const PAGE_SIZE = 50;

/** A group is one {section}/{slug} — the collection of all its locale rows. */
type PageGroup = {
  key: string;              // "blog/best-ipfs-pinning-services"
  section: 'landing' | 'blog' | 'docs';
  slug: string;
  displayName: string;      // best-of-any title across locales, fallback to slug — used for sort + display
  locales: IndexRow[];      // all locale rows for this page
  aggregate: {
    clicks: number;
    impressions: number;
    ctr: number;            // weighted: sum(clicks)/sum(imp)
    position: number | null; // weighted by impressions; null if all zero
    indexingSummary: 'PASS' | 'MIXED' | 'FAIL' | 'UNKNOWN';
    openP0: number;
    openP1: number;
    openP2: number;
    inProgress: number;
    needsResuggest: number;
    watchList: number;
    closedImproved: number;
    closedWorsened: number;
    closedNeutral: number;
    closedOther: number;
    issuesCount: number;    // max across locales — the "worst" locale
  };
  primaryUrl: string | null; // URL of the EN locale if present, else the first
  primaryTitle: string | null;
  // Aggregate content-lifecycle dates: earliest created across locales +
  // latest updated across locales (the group's "lifecycle span")
  createdDate: string | null;
  updatedDate: string | null;
};

function aggregate(rows: IndexRow[]): PageGroup['aggregate'] {
  const clicks = rows.reduce((a, r) => a + (r.gsc?.clicks ?? 0), 0);
  const impressions = rows.reduce((a, r) => a + (r.gsc?.impressions ?? 0), 0);
  const ctr = impressions > 0 ? clicks / impressions : 0;
  const posSum = rows.reduce((a, r) => a + ((r.gsc?.position ?? 0) * (r.gsc?.impressions ?? 0)), 0);
  const position = impressions > 0 ? posSum / impressions : null;

  const indexingStates = new Set(rows.map((r) => r.gsc?.indexing).filter(Boolean) as string[]);
  let indexingSummary: PageGroup['aggregate']['indexingSummary'] = 'UNKNOWN';
  if (indexingStates.size === 0) indexingSummary = 'UNKNOWN';
  else if (indexingStates.size === 1) {
    const only = [...indexingStates][0];
    indexingSummary = only === 'PASS' ? 'PASS' : only === 'FAIL' ? 'FAIL' : 'MIXED';
  } else {
    indexingSummary = 'MIXED';
  }

  return {
    clicks, impressions, ctr, position, indexingSummary,
    openP0:      rows.reduce((a, r) => a + r.todos.openP0, 0),
    openP1:      rows.reduce((a, r) => a + r.todos.openP1, 0),
    openP2:      rows.reduce((a, r) => a + r.todos.openP2, 0),
    inProgress:  rows.reduce((a, r) => a + r.todos.inProgress, 0),
    needsResuggest: rows.reduce((a, r) => a + (r.todos.needsResuggest ?? 0), 0),
    watchList:   rows.reduce((a, r) => a + (r.todos.watchList ?? 0), 0),
    closedImproved: rows.reduce((a, r) => a + r.todos.closedImproved, 0),
    closedWorsened: rows.reduce((a, r) => a + r.todos.closedWorsened, 0),
    closedNeutral:  rows.reduce((a, r) => a + r.todos.closedNeutral, 0),
    closedOther:    rows.reduce((a, r) => a + r.todos.closedOther, 0),
    issuesCount:    Math.max(...rows.map((r) => r.audit.issuesCount), 0),
  };
}

function buildGroups(rows: IndexRow[]): PageGroup[] {
  const byKey = new Map<string, IndexRow[]>();
  for (const r of rows) {
    const key = `${r.section}/${r.slug}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key)!.push(r);
  }
  const groups: PageGroup[] = [];
  for (const [key, locales] of byKey) {
    const en = locales.find((r) => r.locale === 'en') || locales[0];
    // Display name preference: EN title > any locale's title > slug
    const displayName = en.title || locales.find((r) => r.title)?.title || en.slug;
    groups.push({
      key,
      section: en.section,
      slug: en.slug,
      displayName,
      locales: [...locales].sort((a, b) => {
        // en first, then alphabetical
        if (a.locale === 'en') return -1;
        if (b.locale === 'en') return 1;
        return a.locale.localeCompare(b.locale);
      }),
      aggregate: aggregate(locales),
      primaryUrl: en.url,
      primaryTitle: en.title,
      createdDate: locales.map((r) => r.createdDate).filter(Boolean).sort()[0] || null,
      updatedDate: locales.map((r) => r.updatedDate).filter(Boolean).sort().pop() || null,
    });
  }
  return groups;
}

function formatPct(v: number | null) {
  if (v == null) return '—';
  return `${(v * 100).toFixed(2)}%`;
}

function DeltaArrow({ value, invert = false }: { value: number | null; invert?: boolean }) {
  if (value == null || Math.abs(value) < 0.05) return <span className="text-gray-300">→</span>;
  const goodDirection = invert ? value < 0 : value > 0;
  const color = goodDirection ? 'text-green-600' : 'text-red-600';
  const arrow = value > 0 ? '↑' : '↓';
  return <span className={color}>{arrow}{Math.abs(value).toFixed(2)}</span>;
}

export function SeoTrackingTable() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['seo-tracking-index'],
    queryFn: fetchIndex,
    staleTime: 5 * 60 * 1000,
  });

  const [section, setSection] = useState<Section>('all');
  const [locale, setLocale] = useState<string>('all');
  const [priority, setPriority] = useState<PriorityFilter>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('priority');
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  // Which groups are expanded. Default: none (all collapsed). Locale
  // filter auto-expands any group with a matching locale so the filter
  // is visible without click-through.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Sync openId with URL query param for shareability
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('detail');
    if (id) setOpenId(id);
  }, []);
  useEffect(() => {
    const url = new URL(window.location.href);
    if (openId) url.searchParams.set('detail', openId);
    else url.searchParams.delete('detail');
    window.history.replaceState(null, '', url.toString());
  }, [openId]);

  const locales = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.pages.map((p) => p.locale))).sort();
  }, [data]);

  const filtered = useMemo<IndexRow[]>(() => {
    if (!data) return [];
    return data.pages.filter((p) => {
      if (section !== 'all' && p.section !== section) return false;
      if (locale !== 'all' && p.locale !== locale) return false;
      if (priority === 'has-p0' && p.todos.openP0 === 0) return false;
      if (priority === 'has-p1' && p.todos.openP1 === 0) return false;
      if (priority === 'has-needs-resuggest' && (p.todos.needsResuggest ?? 0) === 0) return false;
      if (search) {
        // Search matches slug / URL / title of the page. Filter runs
        // BEFORE grouping + pagination, so a match on ANY locale of a
        // page's group keeps the whole group visible in the table
        // (regardless of which page it lands on).
        const q = search.toLowerCase();
        if (!p.slug.toLowerCase().includes(q) &&
            !(p.url || '').toLowerCase().includes(q) &&
            !(p.title || '').toLowerCase().includes(q) &&
            !p.section.toLowerCase().includes(q) &&
            !p.locale.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [data, section, locale, priority, search]);

  // Build groups from filtered rows, then sort at the GROUP level.
  const groups = useMemo<PageGroup[]>(() => buildGroups(filtered), [filtered]);

  const sortedGroups = useMemo<PageGroup[]>(() => {
    const arr = [...groups];
    arr.sort((a, b) => {
      switch (sortKey) {
        case 'priority':
          if (a.aggregate.openP0 !== b.aggregate.openP0) return b.aggregate.openP0 - a.aggregate.openP0;
          if (a.aggregate.openP1 !== b.aggregate.openP1) return b.aggregate.openP1 - a.aggregate.openP1;
          return b.aggregate.clicks - a.aggregate.clicks;
        case 'clicks':       return b.aggregate.clicks - a.aggregate.clicks;
        case 'impressions':  return b.aggregate.impressions - a.aggregate.impressions;
        case 'ctr':          return b.aggregate.ctr - a.aggregate.ctr;
        case 'position':     return (a.aggregate.position ?? 999) - (b.aggregate.position ?? 999);
        case 'issues':       return b.aggregate.issuesCount - a.aggregate.issuesCount;
        case 'closed':       return (b.aggregate.closedImproved + b.aggregate.closedWorsened + b.aggregate.closedNeutral) - (a.aggregate.closedImproved + a.aggregate.closedWorsened + a.aggregate.closedNeutral);
        case 'needs-resuggest': return b.aggregate.needsResuggest - a.aggregate.needsResuggest;
      }
    });
    return arr;
  }, [groups, sortKey]);

  // Auto-expand groups when a specific locale filter is active (so the
  // filtered locale is visible without an extra click).
  const effectiveExpanded = useMemo<Set<string>>(() => {
    if (locale === 'all') return expandedGroups;
    const auto = new Set(expandedGroups);
    for (const g of groups) {
      if (g.locales.some((r) => r.locale === locale)) auto.add(g.key);
    }
    return auto;
  }, [expandedGroups, locale, groups]);

  const pageGroups = sortedGroups.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(sortedGroups.length / PAGE_SIZE) || 1;

  useEffect(() => { setPage(0); }, [section, locale, priority, search, sortKey]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Failed to load SEO-tracking index. Confirm <code>/seo-tracking-index.json</code> is deployed.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Legend — explains the badge shorthand used in the Open + Closed columns */}
      <details className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600">
        <summary className="cursor-pointer select-none font-semibold uppercase text-gray-500">Legend</summary>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <div>
            <div className="mb-1 font-semibold uppercase text-gray-500 text-[10px]">Open TODOs (priority)</div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex rounded bg-red-100 px-1.5 py-0.5 font-semibold text-red-700">P0×N</span>
              <span>= urgent, this week</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-2">
              <span className="inline-flex rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-700">P1×N</span>
              <span>= do this month</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-2">
              <span className="inline-flex rounded bg-blue-100 px-1.5 py-0.5 font-semibold text-blue-700">⏳N</span>
              <span>= in progress (executed, awaiting outcome)</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-2">
              <span className="inline-flex rounded bg-orange-100 px-1.5 py-0.5 font-semibold text-orange-700">⚠N</span>
              <span>= needs re-suggest (applier-rejected, next Mon's suggest will rewrite)</span>
            </div>
          </div>
          <div>
            <div className="mb-1 font-semibold uppercase text-gray-500 text-[10px]">Closed TODOs (outcome)</div>
            <div className="flex flex-wrap gap-2"><span className="text-green-700 font-semibold">✓N</span><span>= IMPROVED (signal moved past noise band, right direction)</span></div>
            <div className="mt-1 flex flex-wrap gap-2"><span className="text-red-700 font-semibold">✗N</span><span>= WORSENED (signal moved past noise band, wrong direction)</span></div>
            <div className="mt-1 flex flex-wrap gap-2"><span className="text-gray-500 font-semibold">·N</span><span>= NEUTRAL (within noise band or insufficient impressions)</span></div>
            <div className="mt-1 flex flex-wrap gap-2"><span className="text-gray-400 font-semibold">↩N</span><span>= closed without outcome measurement (bulk-shipped, pre-learning-loop)</span></div>
          </div>
        </div>
        <div className="mt-3 text-[11px] text-gray-500">
          <strong>Trend arrows</strong> next to CTR/Position show week-over-week delta.
          Green = good direction (CTR ↑, position ↓), red = bad direction. Hidden when delta is below noise band.
          <strong className="ml-3">Indexing badge:</strong> PASS = indexed; FAIL = de-indexed; NEUTRAL = crawled-not-indexed; UNKNOWN = no GSC data yet.
        </div>
      </details>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <span className="text-xs font-medium uppercase text-gray-400">Filter</span>
        <select value={section} onChange={(e) => setSection(e.target.value as Section)} className="rounded-md border border-gray-300 px-2 py-1 text-sm">
          <option value="all">All sections</option>
          <option value="landing">Landing</option>
          <option value="blog">Blog</option>
          <option value="docs">Docs</option>
        </select>
        <select value={locale} onChange={(e) => setLocale(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1 text-sm">
          <option value="all">All locales</option>
          {locales.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value as PriorityFilter)} className="rounded-md border border-gray-300 px-2 py-1 text-sm">
          <option value="all">All</option>
          <option value="has-p0">Has open P0</option>
          <option value="has-p1">Has open P1</option>
          <option value="has-needs-resuggest">Has needs re-suggest</option>
        </select>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title / slug / URL / section / locale…"
          className="flex-1 min-w-[200px] rounded-md border border-gray-300 px-2 py-1 text-sm"
          title="Searches across ALL tracked pages (not just the current page of results). Matches on title, slug, URL, section, or locale."
        />
        <div className="text-xs text-gray-500">
          Showing <span className="font-semibold">{pageGroups.length}</span> of <span className="font-semibold">{sortedGroups.length}</span> page groups
          {' · '}
          <span className="font-semibold">{filtered.length}</span> total page-locales
          {' · '}
          <span className="font-semibold">{sortedGroups.filter((g) => g.aggregate.openP0 > 0).length}</span> groups with open P0
        </div>
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => setExpandedGroups(new Set(pageGroups.map((g) => g.key)))}
            className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            Expand all
          </button>
          <button
            onClick={() => setExpandedGroups(new Set())}
            className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            Collapse all
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left" style={{ width: '18px' }}></th>
              <th className="px-3 py-2 text-left">Page / Locale</th>
              <SortHeader label="Open" sortKey="priority" current={sortKey} onChange={setSortKey} />
              <SortHeader label="Clicks" sortKey="clicks" current={sortKey} onChange={setSortKey} />
              <SortHeader label="Imp" sortKey="impressions" current={sortKey} onChange={setSortKey} />
              <SortHeader label="CTR" sortKey="ctr" current={sortKey} onChange={setSortKey} />
              <SortHeader label="Pos" sortKey="position" current={sortKey} onChange={setSortKey} />
              <th className="px-3 py-2 text-left">Indexed?</th>
              <SortHeader label="Issues" sortKey="issues" current={sortKey} onChange={setSortKey} />
              <SortHeader label="Closed" sortKey="closed" current={sortKey} onChange={setSortKey} />
            </tr>
          </thead>
          <tbody>
            {pageGroups.map((g) => {
              const isExpanded = effectiveExpanded.has(g.key);
              const shownLocales = locale === 'all' ? g.locales : g.locales.filter((r) => r.locale === locale);
              const localeCount = g.locales.length;
              return (
                <>
                  {/* Group header row */}
                  <tr
                    key={g.key}
                    onClick={() => toggleGroup(g.key)}
                    className={`cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${isExpanded ? 'bg-gray-50' : 'bg-white'}`}
                  >
                    <td className="px-3 py-2 text-gray-400 select-none">{isExpanded ? '▼' : '▶'}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-900">
                        {g.displayName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {g.section}
                        {' · '}
                        <code className="rounded bg-gray-100 px-1 py-0.5 text-[11px] text-gray-600">{g.slug}</code>
                        {' · '}
                        <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-700">{localeCount} locale{localeCount !== 1 ? 's' : ''}</span>
                        {g.primaryUrl && <> · <a href={g.primaryUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary-600 hover:underline">{g.primaryUrl.replace(/^https:\/\/[^/]+/, '')}</a></>}
                        {(g.createdDate || g.updatedDate) && (
                          <> · <span className="text-gray-400">
                            {g.createdDate && <>created {g.createdDate}</>}
                            {g.createdDate && g.updatedDate && g.createdDate !== g.updatedDate && <>, </>}
                            {g.updatedDate && g.updatedDate !== g.createdDate && <>updated {g.updatedDate}</>}
                          </span></>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {g.aggregate.openP0 > 0 && <span className="mr-1 inline-flex rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-700">P0×{g.aggregate.openP0}</span>}
                      {g.aggregate.openP1 > 0 && <span className="mr-1 inline-flex rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700">P1×{g.aggregate.openP1}</span>}
                      {g.aggregate.inProgress > 0 && <span className="mr-1 inline-flex rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700">⏳{g.aggregate.inProgress}</span>}
                      {g.aggregate.needsResuggest > 0 && <span className="mr-1 inline-flex rounded bg-orange-100 px-1.5 py-0.5 text-xs font-semibold text-orange-700" title="Applier-rejected, awaiting next suggest cycle">⚠{g.aggregate.needsResuggest}</span>}
                      {g.aggregate.watchList > 0 && <span className="mr-1 inline-flex rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold text-gray-700" title="Borderline signals, awaiting check-back">👁{g.aggregate.watchList}</span>}
                    </td>
                    <td className="px-3 py-2 tabular-nums font-semibold">{g.aggregate.clicks || '—'}</td>
                    <td className="px-3 py-2 tabular-nums font-semibold">{g.aggregate.impressions || '—'}</td>
                    <td className="px-3 py-2 tabular-nums font-semibold">{g.aggregate.impressions > 0 ? formatPct(g.aggregate.ctr) : '—'}</td>
                    <td className="px-3 py-2 tabular-nums font-semibold">{g.aggregate.position != null ? g.aggregate.position.toFixed(1) : '—'}</td>
                    <td className="px-3 py-2">
                      <IndexingSummaryBadge value={g.aggregate.indexingSummary} />
                    </td>
                    <td className="px-3 py-2 tabular-nums font-semibold">{g.aggregate.issuesCount || '—'}</td>
                    <td className="px-3 py-2 tabular-nums font-semibold">
                      {g.aggregate.closedImproved > 0 && <span className="mr-1 text-green-700">✓{g.aggregate.closedImproved}</span>}
                      {g.aggregate.closedWorsened > 0 && <span className="mr-1 text-red-700">✗{g.aggregate.closedWorsened}</span>}
                      {g.aggregate.closedNeutral > 0 && <span className="mr-1 text-gray-500">·{g.aggregate.closedNeutral}</span>}
                      {g.aggregate.closedOther > 0 && <span className="mr-1 text-gray-400">↩{g.aggregate.closedOther}</span>}
                    </td>
                  </tr>
                  {/* Locale child rows (rendered when expanded) */}
                  {isExpanded && shownLocales.map((r) => (
                    <tr
                      key={r.id}
                      onClick={(e) => { e.stopPropagation(); setOpenId(r.id); }}
                      className="cursor-pointer border-b border-gray-100 bg-white last:border-b-0 hover:bg-blue-50"
                    >
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2 pl-8">
                        <div className="text-sm text-gray-700">
                          <span className="mr-2 inline-block rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 uppercase">{r.locale}</span>
                          {r.title && <span className="text-gray-600">{r.title.slice(0, 60)}{r.title.length > 60 ? '…' : ''}</span>}
                        </div>
                        {(r.createdDate || r.updatedDate) && (
                          <div className="mt-0.5 text-[10px] text-gray-400 pl-8">
                            {r.createdDate && <>created {r.createdDate}</>}
                            {r.createdDate && r.updatedDate && r.createdDate !== r.updatedDate && <> · updated {r.updatedDate}</>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.todos.openP0 > 0 && <span className="mr-1 inline-flex rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-700">P0×{r.todos.openP0}</span>}
                        {r.todos.openP1 > 0 && <span className="mr-1 inline-flex rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700">P1×{r.todos.openP1}</span>}
                        {r.todos.inProgress > 0 && <span className="mr-1 inline-flex rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700">⏳{r.todos.inProgress}</span>}
                        {(r.todos.needsResuggest ?? 0) > 0 && <span className="mr-1 inline-flex rounded bg-orange-100 px-1.5 py-0.5 text-xs font-semibold text-orange-700">⚠{r.todos.needsResuggest}</span>}
                        {(r.todos.watchList ?? 0) > 0 && <span className="mr-1 inline-flex rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold text-gray-700">👁{r.todos.watchList}</span>}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-gray-700">{r.gsc?.clicks ?? '—'}</td>
                      <td className="px-3 py-2 tabular-nums text-gray-700">{r.gsc?.impressions ?? '—'}</td>
                      <td className="px-3 py-2 tabular-nums text-gray-700">
                        {formatPct(r.gsc?.ctr ?? null)}{' '}
                        {r.gsc?.deltaCtrPp != null && <DeltaArrow value={r.gsc.deltaCtrPp} />}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-gray-700">
                        {r.gsc?.position != null ? r.gsc.position.toFixed(1) : '—'}{' '}
                        {r.gsc?.deltaPosition != null && <DeltaArrow value={r.gsc.deltaPosition} invert />}
                      </td>
                      <td className="px-3 py-2">
                        <IndexingBadge value={r.gsc?.indexing} />
                      </td>
                      <td className="px-3 py-2 tabular-nums text-gray-700">{r.audit.issuesCount || '—'}</td>
                      <td className="px-3 py-2 tabular-nums text-gray-700">
                        {r.todos.closedImproved > 0 && <span className="mr-1 text-green-700">✓{r.todos.closedImproved}</span>}
                        {r.todos.closedWorsened > 0 && <span className="mr-1 text-red-700">✗{r.todos.closedWorsened}</span>}
                        {r.todos.closedNeutral > 0 && <span className="mr-1 text-gray-500">·{r.todos.closedNeutral}</span>}
                        {r.todos.closedOther > 0 && <span className="mr-1 text-gray-400">↩{r.todos.closedOther}</span>}
                      </td>
                    </tr>
                  ))}
                </>
              );
            })}
            {pageGroups.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-gray-400">No pages match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
        >
          ← Prev
        </button>
        <div className="text-sm text-gray-500">
          Page <span className="font-semibold">{page + 1}</span> of <span className="font-semibold">{totalPages}</span>
        </div>
        <button
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          disabled={page >= totalPages - 1}
          className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
        >
          Next →
        </button>
      </div>

      {openId && <SeoTrackingDetail id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function SortHeader({ label, sortKey, current, onChange }: { label: string; sortKey: SortKey; current: SortKey; onChange: (k: SortKey) => void }) {
  const active = current === sortKey;
  return (
    <th
      onClick={() => onChange(sortKey)}
      className={`cursor-pointer px-3 py-2 text-left select-none hover:text-gray-900 ${active ? 'text-gray-900' : ''}`}
    >
      {label} {active && '▾'}
    </th>
  );
}

function IndexingBadge({ value }: { value?: string }) {
  if (!value) return <span className="text-xs text-gray-300">—</span>;
  const colorMap: Record<string, string> = {
    PASS: 'bg-green-100 text-green-700',
    NEUTRAL: 'bg-amber-100 text-amber-700',
    FAIL: 'bg-red-100 text-red-700',
    UNKNOWN: 'bg-gray-100 text-gray-500',
  };
  const color = colorMap[value] || 'bg-gray-100 text-gray-500';
  return <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-semibold ${color}`}>{value}</span>;
}

function IndexingSummaryBadge({ value }: { value: 'PASS' | 'MIXED' | 'FAIL' | 'UNKNOWN' }) {
  const colorMap = {
    PASS:    'bg-green-100 text-green-700',
    MIXED:   'bg-amber-100 text-amber-700',
    FAIL:    'bg-red-100 text-red-700',
    UNKNOWN: 'bg-gray-100 text-gray-500',
  };
  return <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-semibold ${colorMap[value]}`} title={value === 'MIXED' ? 'Locales differ — expand to inspect' : undefined}>{value}</span>;
}
